import { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { authClient } from '@/shared/lib/betterAuth/auth-client'
import {
  OTP_TYPE_SIGN_IN,
  OTP_EMAIL_KEY,
  EMAIL_REGEX,
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_PROFILE_KEY,
} from '@/shared/constants/auth'
import { ROUTES } from '@/shared/constants/routes'
import { onboardingRedirect } from '@/shared/helpers/onboarding-redirect'
import { sendEvent } from '@/shared/hooks/use-analytics'
import { Route } from './page.login.route'
import { removeSecureItem } from '@/shared/lib/storage'
import type { BetterAuthUser } from '@/shared/types/global.types'
import { createUserSession } from '@/shared/lib/betterAuth/create-user-session'

const isValidEmail = (value: string) => EMAIL_REGEX.test(value)

export function useLoginPage() {
  const {
    email: initialEmail = '',
    redirect = '',
    token = '',
    sessionToken = '',
  } = Route.useSearch() as {
    email?: string
    redirect?: string
    token?: string
    sessionToken?: string
  }
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [email, setEmail] = useState(initialEmail)
  const [errorKey, setErrorKey] = useState('')
  const [loadingSend, setLoadingSend] = useState(false)
  const [loadingMagicLink, setLoadingMagicLink] = useState(!!token)
  const [loadingSessionToken, setLoadingSessionToken] = useState(!!sessionToken)
  const submitCalledRef = useRef(false)
  const magicLinkCalledRef = useRef(false)
  const sessionTokenCalledRef = useRef(false)

  useEffect(() => {
    if (!token || magicLinkCalledRef.current) return
    magicLinkCalledRef.current = true

    async function verifyMagicLink() {
      setLoadingMagicLink(true)
      try {
        const result = await authClient.magicLink.verify({
          query: { token },
        })

        if (result.error) {
          setLoadingMagicLink(false)
          navigate({
            to: ROUTES.LOGIN,
            search: {
              email: undefined,
              redirect: undefined,
              token: undefined,
              sessionToken: undefined,
            },
          })
          return
        }

        const data = result.data as {
          token: string
          session?: { token: string }
          user?: BetterAuthUser
        }

        if (!data?.token) {
          setLoadingMagicLink(false)
          navigate({
            to: ROUTES.LOGIN,
            search: {
              email: undefined,
              redirect: undefined,
              token: undefined,
              sessionToken: undefined,
            },
          })
          return
        }

        const { identifyEmail } = await createUserSession(data)
        sendEvent('logged_in_with_magic_link', { email: identifyEmail })
        const redirectUrl = await onboardingRedirect(redirect || ROUTES.HOME)
        navigate({ href: redirectUrl })
      } catch {
        await removeSecureItem(AUTH_TOKEN_KEY)
        await removeSecureItem(REFRESH_TOKEN_KEY)
        await removeSecureItem(USER_PROFILE_KEY)
        setLoadingMagicLink(false)
        navigate({
          to: ROUTES.LOGIN,
          search: {
            email: undefined,
            redirect: undefined,
            token: undefined,
            sessionToken: undefined,
          },
        })
      }
    }

    verifyMagicLink()
  }, [token, redirect])

  useEffect(() => {
    if (!sessionToken || token || sessionTokenCalledRef.current) return
    sessionTokenCalledRef.current = true

    async function handleSessionToken() {
      setLoadingSessionToken(true)
      try {
        const { identifyEmail } = await createUserSession({
          token: sessionToken,
        })
        sendEvent('logged_in_with_session_token', { email: identifyEmail })
        const redirectUrl = await onboardingRedirect(redirect || ROUTES.HOME)
        navigate({ href: redirectUrl })
      } catch {
        await removeSecureItem(AUTH_TOKEN_KEY)
        await removeSecureItem(REFRESH_TOKEN_KEY)
        await removeSecureItem(USER_PROFILE_KEY)
        setLoadingSessionToken(false)
        navigate({
          to: ROUTES.LOGIN,
          search: {
            email: undefined,
            redirect: undefined,
            token: undefined,
            sessionToken: undefined,
          },
        })
      }
    }

    handleSessionToken()
  }, [sessionToken, redirect])

  const handleEmailSubmit = useCallback(async () => {
    if (!isValidEmail(email)) {
      setErrorKey('login.error')
      return
    }
    if (submitCalledRef.current) return
    submitCalledRef.current = true
    setErrorKey('')
    setLoadingSend(true)
    try {
      const { error } = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: OTP_TYPE_SIGN_IN,
      })
      setLoadingSend(false)
      if (error) {
        submitCalledRef.current = false
        setErrorKey('login.error')
        sendEvent('request_otp_failure', { email })
        return
      }
      sendEvent('request_otp_success', { email })
      sessionStorage.setItem(OTP_EMAIL_KEY, email)
      navigate({
        to: ROUTES.VERIFY_CODE,
        search: { email: undefined, redirect: redirect || undefined },
      })
    } catch {
      setLoadingSend(false)
      submitCalledRef.current = false
      setErrorKey('login.offline')
    }
  }, [email, redirect, navigate])

  return {
    email,
    setEmail,
    isEmailValid: isValidEmail(email),
    error: errorKey ? t(errorKey) : '',
    loadingSend,
    loadingMagicLink,
    loadingSessionToken,
    handleEmailSubmit,
  }
}
